import { Link } from 'react-router-dom'
import { FileText } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { useAuth } from '@/contexts/AuthContext'
import { lawyersForInbox, type LawyerRef } from '@/lib/hoje'
import { publicationInboxService } from '@/services/publication-inbox-service'
import { teamService } from '@/services/team-service'
import { EmptyLine, HojeSection } from './HojeSection'
import { useHojeLoader } from './useHojeLoader'

export interface NovasPorAdvogado {
  total: number
  advogados: Array<LawyerRef & { novas: number }>
}

/**
 * Contagem pela própria caixa de entrada (uma consulta por advogado), para valer
 * a mesma regra de carteira (OABs + processos monitorados) e de visibilidade.
 * Sem acesso à lista da equipe, mostra só o total e "eu".
 */
async function loadNovas(currentUserId: string | undefined): Promise<NovasPorAdvogado> {
  const members = await teamService.listMembers().catch(() => [])
  let lawyers = lawyersForInbox(members, currentUserId)
  if (lawyers.length === 0 && currentUserId) lawyers = [{ id: 'eu', nome: 'Eu' }]
  const [todas, ...porAdvogado] = await Promise.all([
    publicationInboxService.novas('todos', 1),
    ...lawyers.map((l) => publicationInboxService.novas(l.id, 1)),
  ])
  return { total: todas.total, advogados: lawyers.map((l, i) => ({ ...l, novas: porAdvogado[i].total })) }
}

/** Publicações novas (estado Nova) por advogado; cada linha abre a caixa filtrada. */
export function HojePublicacoes() {
  const { user } = useAuth()
  const state = useHojeLoader(() => loadNovas(user?.id), user?.id)

  return (
    <HojeSection title="Publicações novas" description="Ainda não triadas, por advogado" icon={FileText} href="/publications" state={state}>
      {(data) => (
        <div className="flex flex-col gap-2">
          <p className="text-sm">
            <span className="text-2xl font-semibold tabular-nums">{data.total}</span>{' '}
            <span className="text-muted-foreground">{data.total === 1 ? 'nova no total' : 'novas no total'}</span>
          </p>
          {data.advogados.length === 0 ? (
            <EmptyLine>Nenhum advogado com OAB monitorada.</EmptyLine>
          ) : (
            <ul className="divide-y rounded-md border">
              {data.advogados.map((a) => (
                <li key={a.id}>
                  <Link
                    to={`/publications?advogado=${encodeURIComponent(a.id)}`}
                    className="flex items-center justify-between gap-2 px-3 py-2 text-sm hover:bg-muted/50"
                  >
                    <span className="truncate">{a.nome}</span>
                    <Badge variant={a.novas > 0 ? 'default' : 'outline'} className="tabular-nums">{a.novas}</Badge>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </HojeSection>
  )
}
